import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BookMarked,
  ExternalLink,
  ShieldCheck,
  AlertTriangle,
  Scale,
  FileText,
  HeartHandshake,
  Languages,
  Search,
  Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import BrandMark from "@/components/BrandMark";
import DisclaimerBanner from "@/components/DisclaimerBanner";

/**
 * Página curada de manuales y guías oficiales pro-se descargables.
 * Todos los enlaces son a documentos de dominio público publicados
 * por agencias del gobierno (USCIS/EOIR/DOJ) o por organizaciones
 * 501(c)(3) reconocidas (Florence Project, NIJC, ILRC, Stanford,
 * ACLU, NILC). miapoyo no aloja ni reproduce el contenido — solo
 * lista enlaces directos a las fuentes originales.
 */
const CATEGORIES = [
  {
    id: "i589",
    label: "Asilo I-589 · Auto-representación",
    Icon: FileText,
    color: "bg-amber-500",
    accent: "border-amber-300 bg-amber-50",
  },
  {
    id: "formularios",
    label: "Formularios oficiales USCIS/EOIR",
    Icon: BookMarked,
    color: "bg-blue-600",
    accent: "border-blue-300 bg-blue-50",
  },
  {
    id: "detencion",
    label: "Detención y bond hearings",
    Icon: Scale,
    color: "bg-rose-600",
    accent: "border-rose-300 bg-rose-50",
  },
  {
    id: "derechos",
    label: "Know Your Rights · Derechos",
    Icon: ShieldCheck,
    color: "bg-emerald-600",
    accent: "border-emerald-300 bg-emerald-50",
  },
  {
    id: "credible",
    label: "Miedo creíble y deportación expedita",
    Icon: AlertTriangle,
    color: "bg-orange-600",
    accent: "border-orange-300 bg-orange-50",
  },
];

const RESOURCES = [
  // ---------- ASILO I-589 / AUTO-REPRESENTACIÓN ----------
  {
    cat: "i589",
    title: "Guía pro-se de asilo defensivo en español",
    org: "Stanford Law · Centro Legal de la Raza · CLSEPA",
    year: "2018",
    lang: "Español",
    desc: "Guía paso a paso del formulario I-589 con casos ejemplo de María y Miguel, audiencias en corte, formulario I-765 y apelaciones.",
    url: "https://law.stanford.edu/wp-content/uploads/2017/05/SF-EOIR-Defensive-Asylum-Pro-Se-Guide_Spanish-FINAL-Feb-2018.pdf",
    featured: true,
  },
  {
    cat: "i589",
    title: "Asylum, Withholding & CAT Pro-Se Guide",
    org: "Florence Project (FIRRP)",
    year: "2023",
    lang: "English",
    desc: "Guía oficial de la organización pro-bono más reconocida en Arizona. Cubre asilo, withholding of removal y CAT (Convención contra la Tortura).",
    url: "https://firrp.org/wp-content/uploads/2023/08/Asylum_WOR_CAT-Guide-2013-1.pdf",
  },
  {
    cat: "i589",
    title: "Pro Se Manual for Immigrants in Detention",
    org: "National Immigrant Justice Center (NIJC)",
    year: "Actual",
    lang: "English",
    desc: "Manual para personas detenidas que se representan a sí mismas. Explica defensas, audiencias y opciones de relief.",
    url: "https://immigrantjustice.org/for-immigrants/know-your-rights/pro-se-manual-for-immigrants-in-detention/",
  },
  {
    cat: "i589",
    title: "Guía pro-se para menores no acompañados (UAC)",
    org: "Florence Project",
    year: "2022",
    lang: "Español",
    desc: "Específica para menores que llegaron solos a EE.UU. y deben llenar su propio I-589.",
    url: "https://firrp.org/wp-content/uploads/2022/09/6-UAC-589-Guide_Final_ENG.pdf",
  },

  // ---------- FORMULARIOS OFICIALES ----------
  {
    cat: "formularios",
    title: "Formulario I-589 — Solicitud de Asilo (oficial USCIS)",
    org: "USCIS · Department of Homeland Security",
    year: "Rev. 2024",
    lang: "English",
    desc: "El formulario oficial y las instrucciones actualizadas. Descárgalo directo del gobierno, sin intermediarios.",
    url: "https://www.uscis.gov/i-589",
  },
  {
    cat: "formularios",
    title: "Formulario I-765 — Permiso de Trabajo",
    org: "USCIS · Department of Homeland Security",
    year: "Rev. 2024",
    lang: "English",
    desc: "Solicitud de Employment Authorization Document (EAD). Disponible para solicitantes de asilo con caso pendiente.",
    url: "https://www.uscis.gov/i-765",
  },
  {
    cat: "formularios",
    title: "Listado completo de formularios EOIR",
    org: "DOJ · Executive Office for Immigration Review",
    year: "Vigente",
    lang: "English",
    desc: "Formularios oficiales de la corte de inmigración: EOIR-28 (comparecencia), EOIR-26 (apelación BIA), EOIR-33 (cambio de domicilio), y todos los demás.",
    url: "https://www.justice.gov/eoir/eoir-forms-list",
  },
  {
    cat: "formularios",
    title: "EOIR Self-Help Materials oficial",
    org: "Department of Justice",
    year: "Vigente",
    lang: "English / Español",
    desc: "Materiales de auto-ayuda publicados directamente por el Departamento de Justicia para personas sin abogado.",
    url: "https://www.justice.gov/eoir/self-help-materials",
  },

  // ---------- DETENCIÓN Y BOND ----------
  {
    cat: "detencion",
    title: "Guide to Legal Access in Immigration Detention",
    org: "Acacia Center for Justice",
    year: "2025",
    lang: "English",
    desc: "Manual sobre cómo acceder a recursos legales dentro de centros de detención bajo PBNDS 2011 §&nbsp;6.3.",
    url: "https://acaciajustice.org/wp-content/uploads/2025/03/Guide-to-Legal-Access-in-Immigration-Detention.pdf",
  },
  {
    cat: "detencion",
    title: "Guía para obtener liberación de detención migratoria",
    org: "NIPN-LG (National Immigration Project of NLG)",
    year: "2024",
    lang: "English",
    desc: "Explica detalladamente bond hearings, parole, y todas las vías legales para conseguir liberación de custodia ICE.",
    url: "https://nipnlg.org/sites/default/files/2024-05/2024_Guide-Obtaining-Release-Imm-Detention.pdf",
  },

  // ---------- DERECHOS / KYR ----------
  {
    cat: "derechos",
    title: "Know Your Rights — Inmigrantes",
    org: "American Civil Liberties Union (ACLU)",
    year: "Vigente",
    lang: "Español · English",
    desc: "Derechos constitucionales en encuentros con ICE: en la puerta de tu casa, en la calle, en el trabajo, en el aeropuerto.",
    url: "https://www.aclu.org/know-your-rights/immigrants-rights",
  },
  {
    cat: "derechos",
    title: "Red Cards · Tarjetas Rojas",
    org: "Immigrant Legal Resource Center (ILRC)",
    year: "Vigente",
    lang: "Español · 19 idiomas",
    desc: "Tarjetas plastificadas para llevar contigo con frases legales clave en inglés. Las usan miles de inmigrantes en encuentros reales.",
    url: "https://www.ilrc.org/red-cards",
  },
  {
    cat: "derechos",
    title: "Know Your Rights · Enforcement",
    org: "National Immigration Law Center (NILC)",
    year: "Vigente",
    lang: "Español · English",
    desc: "Recursos sobre qué hacer durante y después de un encuentro con ICE, redadas de trabajo, y cooperación con policía local.",
    url: "https://www.nilc.org/issues/immigration-enforcement/know-your-rights/",
  },

  // ---------- CREDIBLE FEAR / EXPEDITED REMOVAL ----------
  {
    cat: "credible",
    title: "Toolkit to Assist People Facing Expedited Removal",
    org: "ILRC",
    year: "2025",
    lang: "English",
    desc: "Guía actualizada para enfrentar la deportación expedita expandida. Incluye scripts para entrevista de miedo creíble.",
    url: "https://www.ilrc.org/sites/default/files/2025-02/Toolkit%20to%20Assist%20People%20Facing%20Expanded%20Expedited%20Removal_0.pdf",
  },
  {
    cat: "credible",
    title: "Credible Fear Manual",
    org: "NIPN-LG",
    year: "2024",
    lang: "English",
    desc: "Manual técnico sobre la entrevista de miedo creíble, criterios del oficial de asilo, y review ante juez de inmigración.",
    url: "https://immigrationlitigation.org/wp-content/uploads/2024/04/CF-Manual-prod-4.5.24.pdf",
  },
];

export default function ManualesOficiales() {
  const navigate = useNavigate();
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = RESOURCES.filter((r) => {
    if (activeCat !== "all" && r.cat !== activeCat) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.title.toLowerCase().includes(q) ||
      r.org.toLowerCase().includes(q) ||
      r.desc.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-zinc-200 px-5 py-3 flex items-center justify-between">
        <button
          data-testid="manuales-oficiales-back"
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Volver al panel</span>
        </button>
        <BrandMark size="sm" />
      </header>

      <div className="max-w-4xl mx-auto px-5 pt-6">
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-indigo-950 via-blue-900 to-indigo-800 text-white rounded-3xl p-6 sm:p-8 overflow-hidden mb-6">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-amber-400/20 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <p className="text-[10px] sm:text-xs uppercase tracking-widest font-black text-amber-300 mb-2 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Información valiosa · Manuales oficiales
            </p>
            <h1 className="text-2xl sm:text-3xl font-black leading-tight mb-2">
              Guías pro-se y formularios oficiales
            </h1>
            <p className="text-sm text-blue-100 leading-snug">
              Documentos públicos publicados por agencias del gobierno y organizaciones pro-bono
              reconocidas. miapoyo no aloja estos PDFs — son enlaces directos a sus fuentes
              originales para que veas la información en su forma oficial.
            </p>
          </div>
        </div>

        {/* Search + filter tabs */}
        <div className="mb-5">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              data-testid="manuales-oficiales-search"
              type="search"
              placeholder="Buscar por título, organización o tema..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 rounded-xl border-2 border-zinc-200"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveCat("all")}
              data-testid="manuales-cat-all"
              className={`text-xs font-bold px-3 py-2 rounded-lg transition-colors ${
                activeCat === "all"
                  ? "bg-zinc-900 text-white"
                  : "bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-100"
              }`}
            >
              Todos ({RESOURCES.length})
            </button>
            {CATEGORIES.map((c) => {
              const count = RESOURCES.filter((r) => r.cat === c.id).length;
              return (
                <button
                  key={c.id}
                  data-testid={`manuales-cat-${c.id}`}
                  onClick={() => setActiveCat(c.id)}
                  className={`text-xs font-bold px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                    activeCat === c.id
                      ? "bg-zinc-900 text-white"
                      : "bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-100"
                  }`}
                >
                  <c.Icon className="w-3.5 h-3.5" />
                  {c.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Resource list */}
        <div className="space-y-3">
          {filtered.length === 0 && (
            <div className="bg-white border-2 border-dashed border-zinc-200 rounded-2xl p-8 text-center">
              <p className="text-sm text-zinc-500">
                Sin resultados para tu búsqueda. Prueba con otra palabra.
              </p>
            </div>
          )}

          {filtered.map((r) => {
            const cat = CATEGORIES.find((c) => c.id === r.cat);
            return (
              <a
                key={r.url}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`manual-link-${r.cat}-${r.title.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                className={`block bg-white border-2 rounded-2xl p-4 sm:p-5 hover:shadow-md transition-all group ${
                  r.featured ? "border-amber-400 ring-2 ring-amber-200/50" : "border-zinc-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-11 h-11 rounded-xl ${cat?.color} text-white flex items-center justify-center`}>
                    {cat?.Icon && <cat.Icon className="w-5 h-5" strokeWidth={2.5} />}
                  </div>

                  <div className="min-w-0 flex-1">
                    {r.featured && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full mb-1">
                        <Sparkles className="w-2.5 h-2.5" />
                        Destacado
                      </span>
                    )}
                    <h3 className="text-sm sm:text-base font-black text-zinc-900 leading-tight group-hover:underline">
                      {r.title}
                    </h3>
                    <p className="text-xs text-zinc-700 font-bold mt-0.5">{r.org}</p>
                    <p className="text-xs text-zinc-600 leading-snug mt-2">{r.desc}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[10px] text-zinc-500">
                      <span className="font-bold text-zinc-700">{r.year}</span>
                      <span className="inline-flex items-center gap-1">
                        <Languages className="w-3 h-3" />
                        {r.lang}
                      </span>
                      <span className="inline-flex items-center gap-1 text-blue-700 font-bold ml-auto">
                        Abrir
                        <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {/* Notice */}
        <div className="mt-8 bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <HeartHandshake className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-black text-emerald-900 mb-1">¿Por qué confiar en estos enlaces?</p>
              <p className="text-xs text-emerald-900 leading-snug">
                Todos los recursos provienen de agencias federales (USCIS, DOJ/EOIR) o de
                organizaciones 501(c)(3) que llevan décadas haciendo trabajo pro-bono para
                inmigrantes (Florence Project, NIJC, ILRC, ACLU, NILC, Acacia, Stanford Law).
                miapoyo no recibe comisión ni patrocinio por listarlos.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 bg-zinc-100 border border-zinc-200 rounded-xl p-4">
          <p className="text-[11px] text-zinc-600 leading-snug">
            <span className="font-bold">Aviso legal:</span> miapoyo es un asistente automatizado
            de educación legal, no un despacho de abogados. Los manuales aquí listados son
            material educativo público; algunos pueden tener fechas anteriores a 2025 y
            procedimientos que han cambiado. Confirma cualquier plazo o requisito directamente
            con la fuente oficial o con un abogado.
          </p>
        </div>

        <div className="mt-6">
          <DisclaimerBanner />
        </div>
      </div>
    </div>
  );
}
